import { z } from 'zod';

// SEO Analysis schemas
export const seoIssueSchema = z.object({
  type: z.enum(['error', 'warning', 'info']),
  message: z.string(),
  recommendation: z.string().optional(),
});

export const seoCanonicalUrlCheckSchema = z.object({
  exists: z.boolean(),
  isDefaulted: z.boolean(),
  isSelfReferencing: z.boolean(),
  isUnique: z.boolean(),
  value: z.string().optional(),
});

export const seoMetaTitleCheckSchema = z.object({
  exists: z.boolean(),
  isUnique: z.boolean(),
  length: z.number(),
  status: z.enum(['good', 'too-short', 'risky']),
});

export const seoMetaDescriptionCheckSchema = z.object({
  exists: z.boolean(),
  isUnique: z.boolean(),
  length: z.number(),
  status: z.enum(['good', 'weak', 'truncated']),
});

export const seoOpenGraphCheckSchema = z.object({
  descriptionExists: z.boolean(),
  imageExists: z.boolean(),
});

export const seoHeadingCheckSchema = z.object({
  count: z.number(),
  status: z.enum(['good', 'none', 'multiple']).or(z.enum(['good', 'none', 'many'])),
});

export const seoH2CheckSchema = z.object({
  count: z.number(),
  status: z.enum(['good', 'none', 'many']),
});

export const seoH3CheckSchema = z.object({
  count: z.number(),
  status: z.enum(['good', 'none', 'many']),
});

export const seoDatesCheckSchema = z.object({
  lastUpdated: z.string().datetime(),
  daysSinceUpdate: z.number(),
  needsUpdate: z.boolean(),
});

export const seoWordCountCheckSchema = z.object({
  count: z.number(),
  status: z.enum(['good', 'thin']),
});

// Detailed information about an image with SEO issues
export const imageIssueDetailSchema = z.object({
  componentId: z.string(),
  componentType: z.string(),
  componentLabel: z.string(),
  itemIndex: z.number().optional(),
  itemLabel: z.string().optional(),
  imageUrl: z.string(),
  problem: z.enum(['missing-alt', 'default-alt', 'large-image']),
});

export const seoImagesCheckSchema = z.object({
  count: z.number(),
  missingAlt: z.number(),
  largeImages: z.number(),
  defaultAltText: z.number(),
  issues: z.array(imageIssueDetailSchema).default([]),
});

export const seoInternalLinksCheckSchema = z.object({
  count: z.number(),
  hasLinks: z.boolean(),
});

export const seoSitemapCheckSchema = z.object({
  exists: z.boolean(),
  url: z.string(),
});

export const seoRobotsCheckSchema = z.object({
  exists: z.boolean(),
  url: z.string(),
});

export const seoMobileFriendlyCheckSchema = z.object({
  status: z.enum(['good']),
});

export const seoPublishingFreshnessCheckSchema = z.object({
  lastPublishedAt: z.string().nullable(),
  daysSinceLastPublish: z.number(),
  publishedPostCount: z.number(),
  status: z.enum(['active', 'stale', 'inactive', 'no-posts']),
});

export const seoChecksSchema = z.object({
  canonicalUrl: seoCanonicalUrlCheckSchema,
  metaTitle: seoMetaTitleCheckSchema,
  metaDescription: seoMetaDescriptionCheckSchema,
  openGraph: seoOpenGraphCheckSchema,
  h1Tags: seoHeadingCheckSchema,
  h2Tags: seoH2CheckSchema,
  h3Tags: seoH3CheckSchema,
  dates: seoDatesCheckSchema,
  wordCount: seoWordCountCheckSchema,
  images: seoImagesCheckSchema,
  internalLinks: seoInternalLinksCheckSchema,
  incomingInternalLinks: seoInternalLinksCheckSchema,
  sitemap: seoSitemapCheckSchema,
  robots: seoRobotsCheckSchema,
  mobileFriendly: seoMobileFriendlyCheckSchema,
  publishingFreshness: seoPublishingFreshnessCheckSchema.optional(), // Blog-specific
});

export const seoAnalysisResultSchema = z.object({
  pageId: z.string().uuid().optional(),
  postId: z.string().uuid().optional(),
  url: z.string(),
  score: z.number().min(0).max(100),
  issues: z.array(seoIssueSchema),
  checks: seoChecksSchema,
});

export const siteSEOOverviewSchema = z.object({
  siteId: z.string().uuid(),
  siteName: z.string(),
  url: z.string(),
  overview: z.object({
    totalPages: z.number(),
    totalBlogPosts: z.number(),
    averageScore: z.number(),
    totalIssues: z.number(),
    criticalIssues: z.number(),
    pagesWithIssues: z.number(),
    blogPostsWithIssues: z.number(),
  }),
  pages: z.array(
    z.object({
      pageId: z.string().uuid(),
      url: z.string(),
      score: z.number(),
      issueCount: z.number(),
      criticalIssues: z.number(),
    })
  ),
  blogPosts: z.array(
    z.object({
      blogPostId: z.string().uuid(),
      url: z.string(),
      score: z.number(),
      issueCount: z.number(),
      criticalIssues: z.number(),
    })
  ),
});

// Blog SEO Overview schema
export const blogSEOOverviewSchema = z.object({
  blogId: z.string().uuid(),
  blogName: z.string(),
  siteId: z.string().uuid(),
  url: z.string(),
  overview: z.object({
    totalPosts: z.number(),
    publishedPosts: z.number(),
    draftPosts: z.number(),
    averageScore: z.number(),
    totalIssues: z.number(),
    criticalIssues: z.number(),
    postsWithIssues: z.number(),
    publishingFreshness: z
      .object({
        lastPublishedAt: z.string().nullable(),
        daysSinceLastPublish: z.number().nullable(),
        publishedPostCount: z.number(),
        status: z.enum(['active', 'stale', 'inactive', 'no-posts']),
        message: z
          .object({
            type: z.enum(['info', 'warning']),
            message: z.string(),
            recommendation: z.string(),
          })
          .nullable(),
      })
      .optional(),
  }),
  posts: z.array(
    z.object({
      postId: z.string().uuid(),
      title: z.string(),
      status: z.string(),
      url: z.string(),
      score: z.number(),
      issueCount: z.number(),
      criticalIssues: z.number(),
    })
  ),
});

// SEO Analysis type exports
export type SEOIssue = z.infer<typeof seoIssueSchema>;
export type SEOAnalysisResult = z.infer<typeof seoAnalysisResultSchema>;
export type SiteSEOOverview = z.infer<typeof siteSEOOverviewSchema>;
export type BlogSEOOverview = z.infer<typeof blogSEOOverviewSchema>;
export type ImageIssueDetail = z.infer<typeof imageIssueDetailSchema>;
